import { useEffect, useRef, useState } from "react";

/**
 * Shared "Request keys" button used by both the text-row
 * `UtdPlaceholder` in `MessageBubble.tsx` and the media-row
 * `AttachmentUnavailable` chip in `Timeline.tsx`. State transitions:
 *
 *   idle → requesting (disabled, "Requesting…")
 *   → success ("Waiting for keys"; parent flips status when keys arrive)
 *   → failure ("Retry" in danger colour)
 */
export function RequestKeysButton({ onRequestKeys }: { onRequestKeys: () => Promise<void> }) {
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [failed, setFailed] = useState(false);
  // Timeline virtualisation can unmount the row mid-promise; a stale
  // setState after unmount warns in React and leaks closure refs.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = async () => {
    setRequesting(true);
    setFailed(false);
    try {
      await onRequestKeys();
      if (mountedRef.current) setRequested(true);
    } catch (err) {
      console.warn("requestKeysForEvent failed", err);
      if (mountedRef.current) setFailed(true);
    } finally {
      if (mountedRef.current) setRequesting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      disabled={requesting}
      className="chat-button"
      style={{
        height: 32,
        padding: "0 var(--chat-space-3)",
        borderRadius: "var(--chat-radius-sm)",
        border: "none",
        background: "var(--chat-accent-muted)",
        color: failed
          ? "var(--chat-state-danger)"
          : requested
            ? "var(--chat-fg-muted)"
            : "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
        cursor: requesting ? "default" : "pointer",
        flexShrink: 0,
      }}
    >
      {requesting
        ? "Requesting…"
        : requested
          ? "Waiting for keys"
          : failed
            ? "Retry"
            : "Request keys"}
    </button>
  );
}
